/* eslint formatjs/no-literal-string-in-jsx: "error" */
import React, { FC, ReactNode } from "react"
import { Container, FlexContainer, H2, Stack, Text } from "@carrotfertility/carotene"
import { BodySmall } from "../views/atoms/Bodies"
import { useCurrentUser } from "../context/user/UserContext"
import { FormattedMessage } from "react-intl"
import { getPiiProps } from "services/tracking"

interface Thread {
  date: string
  subject: string
}

type ThreadSubjectAndDateProps = {
  thread: Thread
}

type ThreadContainerProps = {
  children: ReactNode | ReactNode[]
  onClick: () => void
}

type ThreadSideTickProps = {
  active: boolean
}

type StyledThreadsListProps = {
  children: React.ReactNode | React.ReactNode[]
}

export function ThreadSideTick({ active }: ThreadSideTickProps): JSX.Element {
  return active ? (
    <Container
      sx={{
        borderLeftStyle: "solid",
        borderWidth: "0.25rem",
        borderColor: "carrotOrange"
      }}
    />
  ) : (
    <Container sx={{ borderLeftStyle: "solid", borderWidth: "0.25rem", borderColor: "transparent" }} />
  )
}
const unorderedListStyle = {
  ul: {
    marginLeft: "0.5em",
    listStyle: "none" /* Remove default bullets */,
    "li::before": {
      color: "black"
    },
    li: {
      margin: "1em 0 1em 1em"
    }
  }
}

export function DefaultThreadMessage(): JSX.Element {
  const { getFirstName } = useCurrentUser()
  return (
    <Stack spacing={"medium"} direction={"column"} sx={{ marginLeft: "41px", marginRight: "16px" }}>
      <H2>
        <FormattedMessage defaultMessage="Get Started with Messages" />
      </H2>
      <BodySmall>
        <FormattedMessage defaultMessage="Hi {memberFirstName}," values={{ memberFirstName: getFirstName() }} />
      </BodySmall>
      <BodySmall>
        <FormattedMessage
          defaultMessage="Welcome! In this space, you’ll find your conversations with us. Send us a message anytime and we’ll do our best
        to respond within one business day. And don't worry — we'll continue to send our replies to your contact email
        address as well."
        />
      </BodySmall>
      <BodySmall>
        <FormattedMessage defaultMessage="Don't see something you're looking for? There are a few reasons that might happen:" />
        <FlexContainer sx={unorderedListStyle}>
          <ul>
            <li>
              <FormattedMessage defaultMessage="Some notifications, such as Carrot Expert chat reminders, are sent only via email." />
            </li>
            <li>
              <FormattedMessage defaultMessage="At this time, only members can receive messages here. If an eligible partner sends us a message, our reply will appear in their email." />
            </li>
          </ul>
        </FlexContainer>
        <FormattedMessage defaultMessage="Best," description="message salutation" />
        <BodySmall>
          <FormattedMessage defaultMessage="Carrot Care Team" />
        </BodySmall>
      </BodySmall>
    </Stack>
  )
}

export const ThreadSubjectAndDate: FC<ThreadSubjectAndDateProps> = ({ thread }): JSX.Element => {
  return (
    <Stack spacing="none" sx={{ marginRight: "18px", overflow: "hidden" }} padding="20px 0px 20px 18px" width="100%">
      <Text
        variant="caps"
        color="black40"
        sx={{
          fontWeight: "425",
          fontSize: "11px",
          letterSpacing: "0.06em"
        }}
      >
        {thread.date}
      </Text>
      <Text
        {...getPiiProps()}
        sx={{
          fontWeight: "325",
          maxWidth: "244px",
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
          display: "block"
        }}
      >
        {thread.subject}
      </Text>
    </Stack>
  )
}

export function ThreadCardContainer({ children, onClick }: ThreadContainerProps): JSX.Element {
  return (
    <FlexContainer
      sx={{
        borderTop: "rgba(0, 0, 0, 0.1) 1px solid",
        overflow: "none",
        cursor: "pointer",
        "&:hover": {
          opacity: "0.5",
          transition: "opacity 0.15s ease-in"
        },
        paddingRight: "18px"
      }}
      onClick={onClick}
      width="100%"
    >
      {children}
    </FlexContainer>
  )
}

export function StyledThreadsList({ children }: StyledThreadsListProps): JSX.Element {
  return (
    <Container
      background="lightPampas"
      minHeight="400px"
      height="100%"
      borderRadius="8px"
      sx={{ overflowX: "hidden", overflowY: "auto" }}
    >
      {children}
    </Container>
  )
}
